import { DetailSection } from "@/components/ui/detail-field";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { StatusPill, type StatusPillTone } from "@/components/ui/status-pill";
import { InlineLoading } from "@/components/ui/data-states";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { BellOff, CircleHelp, Mail } from "lucide-react";

type Motivo = "rol" | "permiso" | "correo";

interface AvisoConfigurado {
  tipo_evento: string;
  nombre: string;
  descripcion: string | null;
  canal: string | null;
  motivo: Motivo;
}

const MOTIVOS: Record<Motivo, { etiqueta: string; tono: StatusPillTone; explica: string }> = {
  rol: { etiqueta: "Por tu rol", tono: "info", explica: "Te llega porque tu rol activo está configurado para recibirlo." },
  permiso: { etiqueta: "Por tu permiso", tono: "purple", explica: "Te llega porque tienes el permiso que lo habilita." },
  correo: { etiqueta: "Por tu correo", tono: "teal", explica: "Tu correo está agregado de forma directa como destinatario." },
};

/** Mis avisos: solo consulta de los avisos por correo que me llegan (RPC `get_mis_avisos_configurados`). */
export function SeccionAvisos({ open }: { open: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ["mis_avisos_configurados"],
    enabled: open,
    retry: false,
    queryFn: async (): Promise<AvisoConfigurado[] | null> => {
      const { data, error } = await (supabase as any).rpc("get_mis_avisos_configurados");
      // La función puede no existir todavía en este ambiente.
      if (error) return null;
      return (data ?? []) as AvisoConfigurado[];
    },
  });

  const porQue = (
    <dl className="space-y-1.5">
      {(Object.keys(MOTIVOS) as Motivo[]).map((k) => (
        <div key={k}>
          <dt className="font-semibold">{MOTIVOS[k].etiqueta}</dt>
          <dd>{MOTIVOS[k].explica}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <DetailSection
      icon={Mail}
      title="Avisos por correo que recibes"
      action={
        <InfoTooltip content={porQue} side="left" align="start">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CircleHelp className="size-4" aria-hidden />
            ¿Por qué me llegan?
          </button>
        </InfoTooltip>
      }
    >
      {isLoading ? (
        <InlineLoading label="Cargando avisos..." />
      ) : !data || data.length === 0 ? (
        <div className="px-6 py-10 text-center sm:-ml-[38px]">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-muted">
            <BellOff className="size-6 text-muted-foreground" aria-hidden />
          </div>
          <p className="text-sm font-semibold text-foreground">
            {data ? "No recibes avisos por correo" : "Esta consulta aún no está disponible"}
          </p>
          <p className="mx-auto mt-1 max-w-sm text-[13px] text-muted-foreground">
            {data
              ? "Tu rol y tus permisos no tienen avisos por correo configurados."
              : "Se activa con la próxima actualización del sistema."}
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {data.map((a) => {
            const m = MOTIVOS[a.motivo] ?? MOTIVOS.rol;
            return (
              <li key={`${a.tipo_evento}-${a.motivo}`} className="space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="min-w-0 break-words text-[13px] font-medium text-foreground">{a.nombre}</p>
                  <StatusPill tone={m.tono} hideDot>{m.etiqueta}</StatusPill>
                </div>
                {a.descripcion && <p className="break-words text-[12px] text-muted-foreground">{a.descripcion}</p>}
                {a.canal && <p className="text-[11.5px] text-muted-foreground">Canal: {a.canal}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </DetailSection>
  );
}
